import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
    ArrowPathIcon,
    ExclamationTriangleIcon,
    CurrencyDollarIcon,
    TruckIcon,
    UsersIcon,
    UserGroupIcon,
    MapPinIcon,
    ClockIcon,
    ArrowTrendingUpIcon,
    ArrowTrendingDownIcon,
    CalendarDaysIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { getAnalyticsSummary } from "../api/analyticsApi";
import type { AnalyticsDateRange } from "../types";

// ============================================================================
// Helpers
// ============================================================================

const fmtCurrency = (amount: number | null | undefined) => {
    if (amount == null) return "—";
    return new Intl.NumberFormat("en-NG", {
        style: "currency",
        currency: "NGN",
        maximumFractionDigits: 0,
    }).format(amount);
};

const fmtNumber = (n: number | null | undefined) => {
    if (n == null) return "—";
    return new Intl.NumberFormat("en-NG").format(n);
};

const GrowthBadge = ({ value }: { value: number | null | undefined }) => {
    if (value == null) return null;
    const isPositive = value >= 0;
    return (
        <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${isPositive
                    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                    : "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400"
                }`}
        >
            {isPositive ? (
                <ArrowTrendingUpIcon className="h-3 w-3" />
            ) : (
                <ArrowTrendingDownIcon className="h-3 w-3" />
            )}
            {Math.abs(value).toFixed(1)}%
        </span>
    );
};

// ============================================================================
// Component
// ============================================================================

export const AnalyticsSummaryRoute = () => {
    const [dateRange, setDateRange] = useState<AnalyticsDateRange>({});

    const { data, isLoading, error, refetch, isFetching } = useQuery({
        queryKey: ["analytics-summary", dateRange],
        queryFn: () => getAnalyticsSummary(dateRange),
    });

    const d = data?.data;

    // ========================================================================
    // Loading / Error
    // ========================================================================

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center dark:border-rose-800 dark:bg-rose-900/20">
                <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-rose-400" />
                <h3 className="mt-3 text-lg font-semibold text-rose-700 dark:text-rose-300">
                    Failed to load analytics summary
                </h3>
                <p className="mt-1 text-sm text-rose-600 dark:text-rose-400">
                    {(error as Error).message}
                </p>
            </div>
        );
    }

    // ========================================================================
    // Render
    // ========================================================================

    return (
        <div className="space-y-6">
            {/* Date Range + Refresh */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <CalendarDaysIcon className="h-4 w-4 text-slate-400" />
                    <input
                        type="date"
                        value={dateRange.startDate ?? ""}
                        onChange={(e) =>
                            setDateRange((p) => ({ ...p, startDate: e.target.value || undefined }))
                        }
                        className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                    />
                    <span className="text-sm text-slate-400">to</span>
                    <input
                        type="date"
                        value={dateRange.endDate ?? ""}
                        onChange={(e) =>
                            setDateRange((p) => ({ ...p, endDate: e.target.value || undefined }))
                        }
                        className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                    />
                </div>
                <Button variant="secondary" size="sm" onClick={() => refetch()} disabled={isFetching}>
                    <ArrowPathIcon className={`mr-1.5 h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
                    Refresh
                </Button>
            </div>

            {/* Revenue Section */}
            <div className="grid gap-4 sm:grid-cols-3">
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <div className="flex items-center gap-3">
                        <div className="rounded-xl bg-emerald-50 p-2 dark:bg-emerald-900/20">
                            <CurrencyDollarIcon className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                        </div>
                        <div>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Total Revenue</p>
                            <p className="text-xl font-bold text-slate-900 dark:text-white">
                                {fmtCurrency(d?.revenue?.total)}
                            </p>
                        </div>
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                        <GrowthBadge value={d?.revenue?.weekOverWeekGrowth} />
                        <span className="text-xs text-slate-400">vs last week</span>
                    </div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <p className="text-sm text-slate-500 dark:text-slate-400">Platform Commission</p>
                    <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                        {fmtCurrency(d?.revenue?.platformCommission)}
                    </p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <p className="text-sm text-slate-500 dark:text-slate-400">Avg Fare</p>
                    <p className="text-xl font-bold text-slate-900 dark:text-white">
                        {fmtCurrency(d?.trips?.averageFare)}
                    </p>
                </div>
            </div>

            {/* Trips & Users */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <div className="flex items-center gap-3">
                        <div className="rounded-xl bg-blue-50 p-2 dark:bg-blue-900/20">
                            <TruckIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Total Trips</p>
                            <p className="text-xl font-bold text-slate-900 dark:text-white">
                                {fmtNumber(d?.trips?.total)}
                            </p>
                        </div>
                    </div>
                    <p className="mt-1 text-xs text-slate-400">
                        {d?.trips?.completionRate?.toFixed(1)}% completion
                    </p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <div className="flex items-center gap-3">
                        <div className="rounded-xl bg-violet-50 p-2 dark:bg-violet-900/20">
                            <UsersIcon className="h-5 w-5 text-violet-600 dark:text-violet-400" />
                        </div>
                        <div>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Total Users</p>
                            <p className="text-xl font-bold text-slate-900 dark:text-white">
                                {fmtNumber(d?.users?.totalUsers)}
                            </p>
                        </div>
                    </div>
                    <p className="mt-1 text-xs text-slate-400">
                        {fmtNumber(d?.users?.newUsers)} new · {d?.users?.retentionRate?.toFixed(1)}% retention
                    </p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <div className="flex items-center gap-3">
                        <div className="rounded-xl bg-amber-50 p-2 dark:bg-amber-900/20">
                            <UserGroupIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                        </div>
                        <div>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Drivers</p>
                            <p className="text-xl font-bold text-slate-900 dark:text-white">
                                {fmtNumber(d?.drivers?.total)}
                            </p>
                        </div>
                    </div>
                    <p className="mt-1 text-xs text-slate-400">
                        {fmtNumber(d?.drivers?.active)} active · {d?.drivers?.averageRating?.toFixed(2)} avg rating
                    </p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <div className="flex items-center gap-3">
                        <div className="rounded-xl bg-cyan-50 p-2 dark:bg-cyan-900/20">
                            <UsersIcon className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
                        </div>
                        <div>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Active Users</p>
                            <p className="text-xl font-bold text-slate-900 dark:text-white">
                                {fmtNumber(d?.users?.activeUsers)}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Top Region + Peak Hours */}
            <div className="grid gap-6 lg:grid-cols-2">
                {/* Top Performing Region */}
                {d?.topPerformingRegion && (
                    <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                        <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                            <MapPinIcon className="h-4 w-4" /> Top Performing Region
                        </h3>
                        <p className="text-lg font-bold text-slate-900 dark:text-white">
                            {d.topPerformingRegion.name}
                        </p>
                        <div className="mt-2 grid grid-cols-2 gap-3">
                            <div>
                                <p className="text-xs text-slate-400">Revenue</p>
                                <p className="text-sm font-medium text-slate-900 dark:text-white">
                                    {fmtCurrency(d.topPerformingRegion.revenue)}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-slate-400">Trips</p>
                                <p className="text-sm font-medium text-slate-900 dark:text-white">
                                    {fmtNumber(d.topPerformingRegion.tripCount)}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Peak Hours */}
                {d?.peakHours && d.peakHours.length > 0 && (
                    <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                        <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                            <ClockIcon className="h-4 w-4" /> Peak Hours
                        </h3>
                        <div className="space-y-3">
                            {d.peakHours.map((ph) => (
                                <div
                                    key={ph.hour}
                                    className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-2.5 dark:bg-slate-800/50"
                                >
                                    <div>
                                        <p className="text-sm font-medium text-slate-900 dark:text-white">
                                            {ph.label}
                                        </p>
                                        <p className="text-xs text-slate-400">Hour {ph.hour}</p>
                                    </div>
                                    <p className="text-sm font-bold text-primary-600 dark:text-primary-400">
                                        {fmtNumber(ph.trips)} trips
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Date range info */}
            {d?.dateRange && (
                <p className="text-right text-xs text-slate-400 dark:text-slate-500">
                    Period: {d.dateRange.startDate} — {d.dateRange.endDate}
                    {d.generatedAt && ` · Generated ${new Date(d.generatedAt).toLocaleString("en-NG")}`}
                </p>
            )}
        </div>
    );
};
